import { UsedStamp } from './UsedStamp'
import { CompanyAvatar } from '@/components/CompanyAvatar'
import { Badge, Button } from '@/components/ui'
import { cn } from '@/lib/cn'
import { FONT_CLASS, headlineSize, resolveOfferStyle } from './pageStyle'
import { shortOfferLabel } from '@shared/offers'
import { type Offer } from '@shared/model'
import { formatSek } from '@/lib/format'
import { brandGradient } from '@/lib/color'

/**
 * En "sida" i bläddringsläget – ett erbjudande som fyller skärmen, som när
 * man bläddrar i pappershäftet.
 *
 * Sidans övre halva kan se ut på fyra sätt. Standard är Lirablått med gul
 * text, men ett företag kan få sin egen bakgrund, sitt eget typsnitt och sin
 * egen rubrik – precis som i pappershäftet, där klubbfärgerna är omslaget
 * och kupongerna inuti är företagens egna.
 */


/**
 * En lång rubrik i 48 px spränger kortet. Skalan väljs i admin, men en text
 * som ändå blir för lång trappas ned automatiskt i stället för att klippas.
 */

export function OfferPageCard({
  offer,
  brandColor,
  remaining,
  owned,
  pageNumber,
  pageCount,
  onOpen,
}: {
  offer: Offer
  /** Företagets profilfärg, när företaget har en. */
  brandColor?: string | null
  remaining: number
  owned: number
  pageNumber: number
  pageCount: number
  onOpen: () => void
}) {
  const usedUp = remaining <= 0

  /*
   * Vad kupongen ska se ut som, i fallande ordning.
   *
   * 1. En färdig design från företaget slår allt. Har de levererat en kupong
   *    är det den som ska synas i häftet, precis som på pappret.
   * 2. Ett uttryckligt val i adminytan.
   * 3. Företagets profilfärg, om de har en. Det gör att profilen slår igenom
   *    utan att någon behöver ställa in den per erbjudande.
   * 4. Lira-standarden.
   */
  const s = resolveOfferStyle(offer, brandColor)
  const {
    couponImage,
    style,
    isCompanyColor,
    isLira,
    hasImage,
    imageUrl,
    headline,
    silent,
    contain,
    onLight,
    headlineColor,
    eyebrowColor,
  } = s

  /*
   * Täckningen är bläddringslägets ensak.
   *
   * Detaljvyn har ingen helsida att fylla – där är kupongen alltid ett band
   * överst – så den bor här och inte i den delade uträkningen.
   *
   * En färdig kupong räknas alltid som helsida: har företaget levererat sin
   * egen design är det den som ska synas, och att klämma in den i övre halvan
   * vore att be om en bild och sedan visa en tredjedel av den.
   */
  const coverage = couponImage ? 'full' : (style.coverage ?? 'header')
  const isFull = coverage === 'full'

  return (
    <article
      className={cn(
        'relative flex h-full flex-col overflow-hidden rounded-card border bg-surface shadow-card',
        usedUp ? 'border-border-strong' : 'border-border',
      )}
      aria-label={`Sida ${pageNumber} av ${pageCount}: ${offer.title} hos ${offer.companyName}${
        usedUp ? ' – redan använd' : ''
      }`}
    >
      {couponImage ? (
        /*
         * En egen sida visas hel, i sin egen yta.
         *
         * Den är företagets färdiga design – text, villkor och allt – och då
         * får ingenting ligga ovanpå den. Tidigare lades vår rubrik och ett
         * raster över bilden, och titeln och knappen täckte dess nederkant.
         * Nu får bilden ytan ovanför panelen, och visas hel hellre än
         * beskuren: en avklippt rad villkor är värre än en smal kant.
         *
         * Formatet är stående, som telefonen. Det tryckta häftet var liggande,
         * men en liggande bild på en stående skärm blir en remsa mitt på sidan.
         */
        <div
          className={cn(
            'relative min-h-0 flex-1 bg-surface-2',
            usedUp && 'grayscale-[0.95] opacity-45',
          )}
        >
          <img
            src={couponImage}
            alt={`${headline} hos ${offer.companyName}`}
            className="absolute inset-0 h-full w-full object-contain"
          />
        </div>
      ) : (
        <>
        {/* Sidans övre halva. Är rabatten förbrukad tas färgen bort helt: ett
            gråskalat huvud syns på en meters håll, till skillnad från en
            nedtonad bricka. */}
        <div
          className={cn(
            'flex flex-col items-center gap-3 px-5 text-center',
            /*
             * Positionen sätts av grenen, inte av bassträngen.
             *
             * Stod `relative` kvar där hamnade båda klasserna på elementet, och
             * eftersom cn bara slår ihop strängar utan att städa motstridiga
             * verktygsklasser avgjordes utfallet av stilmallens ordning – där
             * .relative skrivs efter .absolute och alltså vann. Följden var att
             * inset-0 inte gjorde någonting och "Hela sidan" bara ändrade
             * utfyllnaden.
             */
            // Helsida: designen fyller kortet och vår text lägger sig ovanpå.
            // Halva: ytan får en golvhöjd i stället för att krympa till texten.
            isFull ? 'absolute inset-0 justify-center py-10' : 'relative pt-7 pb-6',
            coverage === 'half' && 'min-h-[52%] justify-center',
            isLira && 'bg-brand-surface',
            style.background === 'light' && 'bg-white',
            style.background === 'dark' && 'bg-[#131320]',
            usedUp && 'grayscale-[0.95] opacity-45',
          )}
          style={
            hasImage
              ? {
                  backgroundImage: `url(${imageUrl})`,
                  backgroundSize: contain ? 'contain' : 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  // Fond bakom en bild som visas hel, så att kanterna inte blir
                  // ett vitt hål. Företagets färg om vi har den.
                  backgroundColor: contain ? (brandColor ?? '#131320') : undefined,
                }
              : isCompanyColor
                ? // Lutning och inte plan färg: en plan mättad yta läser som
                  // affisch, en lutning läser som material. Samma regel som
                  // gäller Lirablå gäller företagens färger.
                  { backgroundImage: brandGradient(brandColor as string) }
                : style.background === 'color' && style.backgroundColor
                  ? { backgroundColor: style.backgroundColor }
                  : undefined
          }
        >
          {/* Raster över bakgrundsbilder. Utan det står texten och faller på
              vilken bild någon råkar ladda upp. */}
          {/*
           * Rastret läggs bara där text faktiskt står.
           *
           * Ett heltäckande raster över hela sidan dämpar företagets bild i
           * onödan. Bär designen texten själv behövs inget raster alls, och vid
           * helsida räcker en toning i underkanten där vår panel ligger.
           */}
          {hasImage && !silent && (
            <div
              aria-hidden
              className={cn(
                'pointer-events-none absolute inset-0',
                isFull
                  ? onLight
                    ? 'bg-gradient-to-t from-white/85 via-white/40 to-transparent'
                    : 'bg-gradient-to-t from-black/80 via-black/35 to-transparent'
                  : onLight
                    ? 'bg-white/55'
                    : 'bg-black/45',
              )}
            />
          )}

          {!silent && (
            <CompanyAvatar
              name={offer.companyName}
              logoUrl={offer.companyLogoUrl}
              size={isFull ? 56 : 68}
              className="relative"
            />
          )}
          {!silent && (
            <p
              className={cn(
                'relative text-sm font-bold tracking-[0.16em] uppercase',
                FONT_CLASS[style.font],
                eyebrowColor,
              )}
            >
              {offer.companyName}
            </p>
          )}
          {!silent && (
            <p
              className={cn(
                'relative leading-none font-extrabold tracking-tight text-balance',
                FONT_CLASS[style.font],
                headlineSize(style, headline),
                headlineColor,
              )}
            >
              {headline}
            </p>
          )}

          {/* Har rubriken skrivits om för hand syns rabattens storlek annars
              ingenstans på sidan. */}
          {!silent && s.customHeadline && (
            <p className={cn('relative text-sm font-semibold', eyebrowColor)}>
              {shortOfferLabel(offer.offerTerms)}
            </p>
          )}
        </div>
        </>
      )}

      {/* Stämpeln läggs tvärs över hela sidan. Samma form som kvittot man
          fick vid inlösen, så att de två läses som samma sak. */}
      {usedUp && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-x-0 flex justify-center',
            isFull ? 'top-[32%]' : 'top-[26%]',
          )}
        >
          {/* Mot en helsidesbild försvinner en fristående stämpel i motivet.
              En dämpad platta bakom håller den läsbar utan att se ut som en
              dialogruta. */}
          <span className={isFull ? 'rounded-card bg-surface/80 px-4 py-2 backdrop-blur-sm' : ''}>
            <UsedStamp size="lg" />
          </span>
        </div>
      )}

      {/*
       * Ramen är medvetet tunn.
       *
       * Här stod tidigare kategoribricka, fyra rader beskrivning och
       * "Sida 3 av 30". Kategorin hör hemma i filtret, beskrivningen finns på
       * erbjudandets egen sida, och sidnumret visar bläddraren redan i sin
       * egen rad. I pappershäftet finns inget av det – där är kupongen hela
       * sidan, och varje rad vi tar bort här är en rad designen får.
       *
       * Kvar står det köparen faktiskt behöver i en butik: vad erbjudandet
       * heter, vad det är värt, hur många uttag som finns, och knappen.
       */}
      <div
        className={cn(
          'flex flex-col gap-2.5',
          couponImage
            ? // Under den egna sidan, inte över den – se kommentaren vid bilden.
              'relative shrink-0 border-t border-border bg-surface p-4'
            : isFull
              ? // Panelen ligger ovanpå designen i underkanten. Egen botten och
                // inte bara text på bild: en knapp som flyter fritt över ett
                // fotografi går inte att hitta med ögat.
                'relative mt-auto bg-surface/92 p-4 backdrop-blur-sm'
              : 'flex-1 p-5',
        )}
      >
        <div>
          <h3
            className={cn(
              'font-display font-extrabold text-balance',
              isFull ? 'text-lg' : 'text-xl',
            )}
          >
            {offer.title}
          </h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Badge tone="gold">Värde ca {formatSek(offer.estimatedValueSek)}</Badge>
            {!usedUp && owned > 1 && <Badge tone="brand">{remaining} kvar</Badge>}
            {/* Bär designen texten står rabattens storlek ingen annanstans
                på sidan där vi kan garantera att den syns. */}
            {silent && <Badge tone="brand">{shortOfferLabel(offer.offerTerms)}</Badge>}
          </div>
        </div>

        {offer.description && !isFull && coverage !== 'half' && (
          <p className="line-clamp-2 text-sm text-muted">{offer.description}</p>
        )}

        <Button
          fullWidth
          size="lg"
          variant={usedUp ? 'quiet' : 'primary'}
          onClick={onOpen}
          className={isFull ? '' : 'mt-auto'}
        >
          {usedUp ? 'Visa detaljer' : 'Öppna erbjudandet'}
        </Button>
      </div>
    </article>
  )
}
